import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { surfaceSx } from "../center/ownerCenterUi";

const RELATIONSHIP_LABELS = {
  prospect: "Prospecto",
  client: "Cliente",
  contact: "Contacto",
  supplier: "Proveedor",
  partner: "Socio",
  personal: "Personal",
  other: "Otro",
};

const STATUS_LABELS = {
  lead: "Potencial",
  active: "Activo",
  inactive: "Inactivo",
};

const CHANNEL_LABELS = {
  "": "Sin canal preferido",
  whatsapp: "WhatsApp",
  phone: "Teléfono",
  email: "Email",
  instagram: "Instagram",
  in_person: "En persona",
  other: "Otro",
};

function emptyForm(client = {}) {
  return {
    name: client.name || "",
    company: client.company || "",
    email: client.email || "",
    phone: client.phone || "",
    status: client.status || "active",
    source: client.source || "",
    relationshipType: client.relationshipType || "client",
    preferredChannel: client.preferredChannel || "",
    notes: client.notes || "",
    tags: Array.isArray(client.tags) ? client.tags.join(", ") : "",
  };
}

function IdentityEditor({ open, client, onClose, onSaved }) {
  const [form, setForm] = React.useState(() => emptyForm(client));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setForm(emptyForm(client));
    setError("");
  }, [open, client]);

  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError("");
    try {
      const response = await ownerApi.updateClient(client.id || client._id, {
        ...form,
        name: form.name.trim(),
        tags: form.tags.split(",").map((value) => value.trim()).filter(Boolean),
      });
      const fallback = { ...client, ...form, tags: form.tags.split(",").map((value) => value.trim()).filter(Boolean) };
      onSaved?.(response?.client || fallback);
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, "No pude actualizar los datos del cliente."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography sx={{ fontWeight: 1000 }}>Editar datos de la relación</Typography>
        <Typography sx={{ mt: .25, color: "text.secondary", fontSize: ".8rem" }}>
          Estos datos pertenecen a tu ficha Owner. La cuenta y los proyectos WebProfit siguen siendo entidades independientes.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.2} sx={{ pt: .8 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <TextField autoFocus fullWidth label="Nombre" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
            <TextField fullWidth label="Empresa / marca" value={form.company} onChange={(event) => setForm({ ...form, company: event.target.value })} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <TextField fullWidth label="Email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
            <TextField fullWidth label="Teléfono" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.1}>
            <FormControl fullWidth>
              <InputLabel>Relación</InputLabel>
              <Select label="Relación" value={form.relationshipType} onChange={(event) => setForm({ ...form, relationshipType: event.target.value })}>
                {Object.entries(RELATIONSHIP_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Estado comercial</InputLabel>
              <Select label="Estado comercial" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                {Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Canal preferido</InputLabel>
              <Select label="Canal preferido" value={form.preferredChannel} onChange={(event) => setForm({ ...form, preferredChannel: event.target.value })}>
                {Object.entries(CHANNEL_LABELS).map(([value, label]) => <MenuItem key={value || "none"} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <TextField fullWidth label="Origen" placeholder="Ej.: referido, Instagram, evento" value={form.source} onChange={(event) => setForm({ ...form, source: event.target.value })} />
            <TextField fullWidth label="Tags" helperText="Separadas por coma" value={form.tags} onChange={(event) => setForm({ ...form, tags: event.target.value })} />
          </Stack>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Contexto estable / nota de ficha"
            helperText="Información estable que querés recordar. Los hechos con fecha se registran en Historial."
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !form.name.trim()} sx={{ textTransform: "none", fontWeight: 950 }}>
          {saving ? "Guardando…" : "Guardar datos"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function OwnerClientIdentityCard({ client, onClientUpdated, autoEditKey = "" }) {
  const [editing, setEditing] = React.useState(false);
  const lastAutoEditKeyRef = React.useRef("");
  const tags = Array.isArray(client?.tags) ? client.tags.filter(Boolean) : [];

  React.useEffect(() => {
    if (!autoEditKey || lastAutoEditKeyRef.current === autoEditKey) return;
    lastAutoEditKeyRef.current = autoEditKey;
    setEditing(true);
  }, [autoEditKey]);

  return (
    <>
      <Box sx={{ ...surfaceSx, p: { xs: 1.6, sm: 2 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.4} alignItems={{ sm: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: "1.3rem", sm: "1.55rem" }, fontWeight: 1000, letterSpacing: "-.03em" }}>
              {client?.name || "Cliente"}
            </Typography>
            <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".84rem", fontWeight: 750 }}>
              {client?.company || "Sin empresa / marca"}
            </Typography>
            <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Chip size="small" label={RELATIONSHIP_LABELS[client?.relationshipType] || "Relación"} sx={{ fontWeight: 900 }} />
              <Chip size="small" variant="outlined" label={STATUS_LABELS[client?.status] || "Sin estado"} sx={{ fontWeight: 900 }} />
              {client?.preferredChannel ? <Chip size="small" variant="outlined" label={`Preferido: ${CHANNEL_LABELS[client.preferredChannel] || client.preferredChannel}`} sx={{ fontWeight: 850 }} /> : null}
              {client?.source ? <Chip size="small" variant="outlined" label={`Origen: ${client.source}`} sx={{ fontWeight: 850 }} /> : null}
            </Stack>
          </Box>
          <Button startIcon={<EditRoundedIcon />} variant="outlined" onClick={() => setEditing(true)} sx={{ textTransform: "none", fontWeight: 900, flex: "0 0 auto" }}>
            Editar datos
          </Button>
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 1, mt: 1.5 }}>
          <Stack direction="row" spacing={.8} alignItems="center">
            <EmailRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
            <Typography sx={{ fontSize: ".82rem", fontWeight: 800, overflowWrap: "anywhere" }}>{client?.email || "Sin email"}</Typography>
          </Stack>
          <Stack direction="row" spacing={.8} alignItems="center">
            <PhoneRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
            <Typography sx={{ fontSize: ".82rem", fontWeight: 800 }}>{client?.phone || "Sin teléfono"}</Typography>
          </Stack>
        </Box>

        {tags.length ? <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>{tags.map((tag) => <Chip key={tag} size="small" label={tag} sx={{ height: 22, fontSize: ".68rem", fontWeight: 850 }} />)}</Stack> : null}

        <Box sx={{ mt: 1.4, p: 1.15, borderRadius: 2.2, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.03)" }}>
          <Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".06em" }}>Contexto estable</Typography>
          <Typography sx={{ mt: .35, fontSize: ".82rem", lineHeight: 1.5, whiteSpace: "pre-wrap", color: client?.notes ? "text.primary" : "text.secondary" }}>
            {client?.notes || "Sin nota de ficha. Usá el historial para conversaciones, reuniones y hechos con fecha."}
          </Typography>
        </Box>
      </Box>
      <IdentityEditor open={editing} client={client} onClose={() => setEditing(false)} onSaved={onClientUpdated} />
    </>
  );
}
